import React, { useEffect, useMemo, useRef } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';

import Field from '../layout/Field.jsx';
import ContentContainer from '../layout/ContentContainer.jsx';
import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';

const QuestionValidator = ({ result = {}, onRetry }) => {
	if (result.isPassed) return <Alert severity="success">Test passed! Your score is {result.score}%!</Alert>;

	if (!result.isPassed)
		return (
			<>
				<Alert severity="error">
					Unfortunately you did not pass the test! Your score is {result.score}%, while the minimum score is{' '}
					{result.minimalScore}% Please reach out to your relevant contactperson for more information
				</Alert>

				{result.maxAttempts > 1 && (
					<Alert
						severity="info"
						sx={{ marginTop: 2 }}
						action={
							result.attempts < result.maxAttempts && (
								<Button color="inherit" size="small" onClick={onRetry}>
									Try again
								</Button>
							)
						}
					>
						You completed attempt {result.attempts} of {result.maxAttempts}
					</Alert>
				)}
			</>
		);
};

const TestSection = ({
	Registry,
	values,
	name,
	id,
	minimalScore = 0,
	maxAttempts = 1,
	fields = [],
	fieldsToDisplay,
	onChange,
	commit = () => {},
	title,
	navigationProps,
	disabled,
	style = {},
}) => {
	const sectionKey = name || id;

	const selectRandomFields = (fieldsArray, count) => {
		const fieldsCopy = fieldsArray.slice();
		let currentIndex = fieldsCopy.length;
		while (currentIndex != 0) {
			let randomIndex = Math.floor(Math.random() * currentIndex);
			currentIndex--;
			[fieldsCopy[currentIndex], fieldsCopy[randomIndex]] = [fieldsCopy[randomIndex], fieldsCopy[currentIndex]];
		}
		return fieldsCopy.slice(0, count);
	};

	const defaultResult = {
		isPassed: false,
		isComplete: false,
		score: 0,
		attempts: 0,
		attemptAnswers: [],
	};

	// Derive all state from props
	const testValues = values[sectionKey]?.testValues ?? {};
	const result = values[sectionKey]?.result ?? defaultResult;

	const lastAttemptAnswers = result.attemptAnswers?.[result.attemptAnswers.length - 1];
	const isValidated = lastAttemptAnswers && JSON.stringify(lastAttemptAnswers) === JSON.stringify(testValues);

	const displayedFields = useMemo(() => {
		const savedFields = values[sectionKey]?.displayedFields;

		// If we have historical data with displayedFields, use those to preserve the original random selection
		if (savedFields) {
			return savedFields;
		}

		return fieldsToDisplay ? selectRandomFields(fields, fieldsToDisplay) : fields;
	}, [values[sectionKey]?.displayedFields, fields, fieldsToDisplay, sectionKey]);

	const hasPersistedDisplayedFields = useRef(false);

	useEffect(() => {
		if (values[sectionKey]?.displayedFields || hasPersistedDisplayedFields.current) return;
		hasPersistedDisplayedFields.current = true;
		commit({
			target: { name: sectionKey, value: { testValues: {}, result: defaultResult, displayedFields } },
		});
	}, [values[sectionKey]?.displayedFields, sectionKey, commit, displayedFields]);

	const handleChange = ({ target }) => {
		const value = { ...testValues, [target.name]: target.value };
		onChange({ target: { name: sectionKey, value: { testValues: value, displayedFields, result } } });
	};

	const handleValidation = async () => {
		const questionsPassed = Object.values(testValues).filter((v) => v.isValid).length;
		const questionsTotal = displayedFields.filter((f) => f.type === 'QuestionMultipleChoice').length;
		const score = Math.round((questionsPassed / questionsTotal) * 100);
		const attempts = result.attempts + 1;
		const isPassed = score >= minimalScore;
		const isComplete = isPassed || attempts >= maxAttempts;
		const r = {
			isPassed,
			isComplete,
			questionsPassed,
			questionsTotal,
			minimalScore,
			score,
			attempts,
			maxAttempts,
			attemptAnswers: [...result.attemptAnswers, { ...testValues }],
		};

		await commit({
			target: {
				name: sectionKey,
				value: { testValues, result: r, isComplete, isFailed: !r.isPassed, displayedFields },
			},
		});
	};

	const handleRetry = () => {
		const newFields = fieldsToDisplay ? selectRandomFields(fields, fieldsToDisplay) : displayedFields;
		onChange({ target: { name: sectionKey, value: { testValues: {}, result, displayedFields: newFields } } });
	};

	const questionFields = displayedFields.filter((f) => f.type === 'QuestionMultipleChoice');
	const canValidate = Object.keys(testValues).length === questionFields.length;
	const showCorrect =
		result.attempts >= result.maxAttempts || result.isPassed || (disabled && !!Object.keys(testValues)?.length);
	const next = isValidated ? navigationProps.next : handleValidation;
	const nextLabel = isValidated ? navigationProps.navigation.nextLabel : 'Check Answers';
	const navigation = { ...navigationProps.navigation, nextLabel };

	return (
		<SectionContainer
			background="light"
			Navigation={
				<SectionNav
					{...navigationProps}
					canContinue={((!isValidated && canValidate) || result.isComplete) && !disabled}
					hasPrevious={!isValidated && !disabled}
					hasNext={!result.isComplete}
					navigation={navigation}
					next={next}
				/>
			}
			style={style}
		>
			<ContentContainer>
				{title && (
					<h2 className="nf-section-title" key={title.replace(' ', '-')}>
						{title}
					</h2>
				)}
				{displayedFields.map((field, i) => (
					<Field
						Registry={Registry}
						key={field.name || i}
						{...field}
						value={testValues[field.name]}
						onChange={handleChange}
						validate={isValidated || (disabled && !!Object.keys(testValues)?.length)}
						showCorrect={showCorrect}
					/>
				))}

				{(isValidated || (disabled && !!Object.keys(testValues)?.length)) && (
					<QuestionValidator onRetry={handleRetry} result={result} />
				)}
			</ContentContainer>
		</SectionContainer>
	);
};

TestSection.type = 'TestSection';
TestSection.hasNavigation = true;
TestSection.spec = [
	{
		name: 'TestSection',
		key: 'TestSection',
		title: 'Section to test knowledge through questions',
		allowedFields: ['Image', 'Typography', 'Link', 'QuestionMultipleChoice'],
		translatable: ['title', 'navigation.previousLabel', 'navigation.nextLabel', 'navigation.submitLabel'],
		fields: [
			{
				type: 'TextField',
				name: 'title',
				label: 'Test title',
			},
			{
				type: 'TextField',
				name: 'name',
				label: 'Test name',
			},
			{
				type: 'NumberTextField',
				htmlType: 'number',
				name: 'minimalScore',
				label: 'Minimum score (0-100) to pass the test',
				min: 0,
				max: 100,
			},
			{
				type: 'NumberTextField',
				htmlType: 'number',
				name: 'maxAttempts',
				label: 'Maximum attempts (0-10) at passing the test',
				min: 0,
				max: 10,
			},
			{
				type: 'NumberTextField',
				htmlType: 'number',
				name: 'fieldsToDisplay',
				label: 'Number of randomly selected fields to be displayed.',
				min: 0,
			},
			{
				type: 'TextField',
				name: 'navigation.previousLabel',
				label: 'Previous label',
				helperText: 'if there is a previous section, this will be the label to go back.',
			},
			{
				type: 'TextField',
				name: 'navigation.nextLabel',
				label: 'Next label',
				helperText: 'if there is a next section, this will be the label to go forward.',
			},
			{
				type: 'TextField',
				name: 'navigation.submitLabel',
				label: 'Submit label',
				helperText: 'If this section is the last section, this will be the text on the submit button.',
			},
		],
		defaultProperties: {
			title: 'Test title',
			navigation: {
				previousLabel: 'Previous',
				nextLabel: 'Next',
				submitLabel: 'Submit',
			},
		},
	},
];

export default TestSection;
